import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { Subsidiary } from '@vy/types';
import { isNotEmptyArray, isNotNullOrUndefined, withDefault } from '@vy/utils';
import React from 'react';
import { VyTable } from '../../../../../components/new-design/table/vy-table/VyTable';
import { useSubsidiaries } from '../../../../../hooks/requests/use-subsidiaries';
import { useDialog } from '../../../../../hooks/use-dialog';
import { formatDate } from '../../../../../utils/date';
import { CreateSubsidiaryDialog } from './entities-tab-content-components/CreateSubsidiaryDialog/CreateSubsidiaryDialog';
import { SyncErpSubsidiary } from './entities-tab-content-components/SyncErpSubsidiary/SyncErpSubsidiary';
import { UpdateSubsidiaryDialog } from './entities-tab-content-components/UpdateSubsidiaryDialog/UpdateSubsidiaryDialog';

export const EntitiesTabContent: React.FC = () => {
  const { data: subsidiariesResponse, isLoading: isLoadingSubsidiaries } = useSubsidiaries({
    suspense: true,
  });

  const subsidiaries = withDefault(subsidiariesResponse?.subsidiaries, []);

  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const [selectedSubsidiary, setSelectedSubsidiary] = React.useState<Subsidiary | undefined>(undefined);

  const handleCloseDialog = () => {
    handleClose();
    setSelectedSubsidiary(undefined);
  };

  return (
    <Stack direction="column" divider={<Divider sx={{ marginY: '20px' }} />} sx={{
      paddingY: "20px"
    }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          paddingX: "20px"
        }}>
        <Stack direction="column" sx={{
          display: "flex"
        }}>
          <Typography variant="h6">Subsidiaries Management</Typography>
          <Typography variant="subtitle1" sx={{
            color: "text.secondary"
          }}>
            Manage your subsidiaries here
          </Typography>
        </Stack>

        <CreateSubsidiaryDialog existingSubsidiaries={subsidiaries}>
          <div>
            <Button variant="contained" color="primary" sx={{ columnGap: '12px' }}>
              <VyIcon icon="PlusMedium" />
              Create New
            </Button>
          </div>
        </CreateSubsidiaryDialog>
      </Stack>
      <div>
        <Stack direction="column" sx={{ maxWidth: "lg", paddingX: "20px", rowGap: "16px" }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography>Subsidiaries</Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              List of entities and subsidiaries related to this account
            </Typography>
          </Box>

          <Box
            sx={{
              // Column-flex container so the VyTable child stretches to its full width
              // (cross-axis stretch). The grid's columns are all `flex: 1` with no
              // intrinsic width, so a zero-width container makes MUI X v9 render an empty
              // table (columnsTotalWidth: 0). See ENG-6137.
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: '300px',
            }}
          >
            <VyTable
              tableName="settings-entities"
              itemsCount={subsidiaries.length}
              items={subsidiaries}
              loading={isLoadingSubsidiaries}
              columns={[
                {
                  field: 'name',
                  headerName: 'Subsidiary',
                  flex: 1,
                  headerClassName: 'pl-6',
                  cellClassName: 'pl-6',
                  minWidth: 200,
                },
                {
                  field: 'createdAt',
                  headerName: 'Date created',
                  flex: 1,
                  headerClassName: 'pl-6',
                  cellClassName: 'pl-6',
                  valueGetter: (_value, row) => formatDate(row.createdAt),
                  minWidth: 200,
                },
                {
                  field: 'taxId',
                  headerName: 'Tax ID',
                  flex: 1,
                  headerClassName: 'pl-6',
                  cellClassName: 'pl-6',
                  minWidth: 200,
                },
              ]}
              showPagination={false}
              onRowClick={({ row: clickedSubsidiary }) => {
                setSelectedSubsidiary(clickedSubsidiary);
                handleOpen();
              }}
            />
          </Box>

          {isNotNullOrUndefined(selectedSubsidiary) && (
            <UpdateSubsidiaryDialog
              isDialogOpen={isDialogOpen}
              handleClose={handleCloseDialog}
              handleOpen={handleOpen}
              allSubsidiaries={subsidiaries}
              subsidiary={selectedSubsidiary}
            />
          )}
        </Stack>

        {isNotEmptyArray(subsidiaries) && (
          <>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                maxWidth: "lg",
                display: "flex",
                paddingX: "20px"
              }}>
              <Stack>
                <Typography>Sync with ERP</Typography>

                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Link Vayu subsidiaries to the right ERP subsidiaries for smooth syncing
                </Typography>
              </Stack>
            </Stack>

            <SyncErpSubsidiary subsidiaries={subsidiaries} />
          </>
        )}
      </div>
    </Stack>
  );
};
